import { Button } from '@/components/ui/button';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { Plus } from 'lucide-react';
import { memo } from 'react';
import {
  Control,
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from 'react-hook-form';
import { Executive, IdentificationFormData } from '../types';
import { ExecutiveCard } from './ExecutiveCard';

interface ExecutivesSectionProps {
  control: Control<IdentificationFormData>;
  fields: FieldArrayWithId<IdentificationFormData, 'executives', 'id'>[];
  append: UseFieldArrayAppend<IdentificationFormData, 'executives'>;
  remove: UseFieldArrayRemove;
  defaultExecutiveValues: Executive;
}

/**
 * Section component for managing executive bodies and authorized representatives.
 * Displays dynamic array of executive cards with add/remove functionality.
 *
 * @param {ExecutivesSectionProps} props - Component properties
 * @returns {JSX.Element} Executive section with dynamic field array
 */
export function ExecutivesSection({
  control,
  fields,
  append,
  remove,
  defaultExecutiveValues,
}: ExecutivesSectionProps) {
  const { t: tForms } = useFormsTranslation();

  return (
    <div className='space-y-6'>
      <div className='flex items-center gap-2 mb-4'>
        <h4 className='text-lg font-semibold'>
          {tForms('identification.sections.executives')}
        </h4>
      </div>
      <div className='text-muted-foreground text-sm'>
        <p>{tForms('identification.descriptions.authorizedSigners')}</p>
      </div>

      {fields.map((field, index) => (
        <ExecutiveCard
          key={field.id}
          control={control}
          index={index}
          onRemove={() => remove(index)}
          canRemove={fields.length > 1}
        />
      ))}

      <Button
        type='button'
        variant='outline'
        onClick={() => append(defaultExecutiveValues)}
        className='w-full'
      >
        <Plus className='w-4 h-4 mr-2' />
        {tForms('identification.buttons.addExecutive')}
      </Button>
    </div>
  );
}

// Memoize component to prevent cascade re-renders from parent
export default memo(ExecutivesSection);
